import _typeof from "@babel/runtime/helpers/typeof";
import _extends from "@babel/runtime/helpers/extends";
import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";

function _toPropertyKey(arg) { var key = _toPrimitive(arg, "string"); return _typeof(key) === "symbol" ? key : String(key); }

function _toPrimitive(input, hint) { if (_typeof(input) !== "object" || input === null) return input; var prim = input[Symbol.toPrimitive]; if (prim !== undefined) { var res = prim.call(input, hint || "default"); if (_typeof(res) !== "object") return res; throw new TypeError("@@toPrimitive must return a primitive value."); } return (hint === "string" ? String : Number)(input); }

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); if (enumerableOnly) symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; }); keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = arguments[i] != null ? arguments[i] : {}; if (i % 2) { ownKeys(Object(source), true).forEach(function (key) { _defineProperty(target, key, source[key]); }); } else if (Object.getOwnPropertyDescriptors) { Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)); } else { ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } } return target; }

import React, { useState, useRef } from 'react';
import Form from '../form';
import arrayMutators from 'final-form-arrays';
import PropTypes from 'prop-types';
import createFocusDecorator from 'final-form-focus';
import RendererContext from '../renderer-context';
import renderForm from './render-form';
import defaultSchemaValidator from '../default-schema-validator';
import SchemaErrorComponent from './schema-error-component';
import defaultValidatorMapper from '../validator-mapper';

var FormRenderer = function FormRenderer(_ref) {
  var componentMapper = _ref.componentMapper,
      FormTemplate = _ref.FormTemplate,
      _onSubmit = _ref.onSubmit,
      onCancel = _ref.onCancel,
      _onReset = _ref.onReset,
      clearOnUnmount = _ref.clearOnUnmount,
      subscription = _ref.subscription,
      clearedValue = _ref.clearedValue,
      schema = _ref.schema,
      validatorMapper = _ref.validatorMapper,
      actionMapper = _ref.actionMapper,
      schemaValidatorMapper = _ref.schemaValidatorMapper,
      props = _objectWithoutProperties(_ref, ["componentMapper", "FormTemplate", "onSubmit", "onCancel", "onReset", "clearOnUnmount", "subscription", "clearedValue", "schema", "validatorMapper", "actionMapper", "schemaValidatorMapper"]);

  var _useState = useState([]),
      _useState2 = _slicedToArray(_useState, 2),
      fileInputs = _useState2[0],
      setFileInputs = _useState2[1];

  var registeredFields = useRef({});
  var focusDecorator = useRef(createFocusDecorator());
  var schemaError;

  var setRegisteredFields = function setRegisteredFields(fn) {
    return registeredFields.current = fn(_objectSpread({}, registeredFields.current));
  };

  var internalRegisterField = function internalRegisterField(name) {
    setRegisteredFields(function (prev) {
      return prev[name] ? _objectSpread(_objectSpread({}, prev), {}, _defineProperty({}, name, prev[name] + 1)) : _objectSpread(_objectSpread({}, prev), {}, _defineProperty({}, name, 1));
    });
  };

  var internalUnRegisterField = function internalUnRegisterField(name) {
    setRegisteredFields(function (_ref2) {
      var currentField = _ref2[name],
          prev = _objectWithoutProperties(_ref2, [name].map(_toPropertyKey));

      return currentField && currentField > 1 ? _objectSpread(_defineProperty({}, name, currentField - 1), prev) : prev;
    });
  };

  var internalGetRegisteredFields = function internalGetRegisteredFields() {
    return Object.entries(registeredFields.current).reduce(function (acc, _ref3) {
      var _ref4 = _slicedToArray(_ref3, 2),
          name = _ref4[0],
          value = _ref4[1];

      return value > 0 ? [].concat(_toConsumableArray(acc), [name]) : acc;
    }, []);
  };

  var validatorMapperMerged = _objectSpread(_objectSpread({}, defaultValidatorMapper), validatorMapper);

  try {
    var validatorTypes = Object.keys(validatorMapperMerged);
    var actionTypes = actionMapper ? Object.keys(actionMapper) : [];
    defaultSchemaValidator(schema, componentMapper, validatorTypes, actionTypes, schemaValidatorMapper);
  } catch (error) {
    schemaError = error; // eslint-disable-next-line no-console

    console.error(error); // eslint-disable-next-line no-console

    console.log('error: ', error.message);
  }

  if (schemaError) {
    return /*#__PURE__*/React.createElement(SchemaErrorComponent, {
      name: schemaError.name,
      message: schemaError.message
    });
  }

  var registerInputFile = function registerInputFile(name) {
    return setFileInputs(function (prevFiles) {
      return [].concat(_toConsumableArray(prevFiles), [name]);
    });
  };

  var unRegisterInputFile = function unRegisterInputFile(name) {
    return setFileInputs(function (prevFiles) {
      return _toConsumableArray(prevFiles.splice(prevFiles.indexOf(name)));
    });
  };

  return /*#__PURE__*/React.createElement(Form, _extends({}, props, {
    onSubmit: function onSubmit(values, formApi) {
      for (var _len = arguments.length, args = new Array(_len > 2 ? _len - 2 : 0), _key = 2; _key < _len; _key++) {
        args[_key - 2] = arguments[_key];
      }

      return _onSubmit.apply(void 0, [values, _objectSpread(_objectSpread({}, formApi), {}, {
        fileInputs: fileInputs
      })].concat(args));
    },
    mutators: _objectSpread({}, arrayMutators),
    decorators: [focusDecorator.current],
    subscription: _objectSpread({
      pristine: true,
      submitting: true,
      valid: true
    }, subscription),
    render: function render(_ref5) {
      var handleSubmit = _ref5.handleSubmit,
          pristine = _ref5.pristine,
          valid = _ref5.valid,
          _ref5$form = _ref5.form,
          reset = _ref5$form.reset,
          mutators = _ref5$form.mutators,
          getState = _ref5$form.getState,
          submit = _ref5$form.submit,
          form = _objectWithoutProperties(_ref5$form, ["reset", "mutators", "getState", "submit"]);

      return /*#__PURE__*/React.createElement(RendererContext.Provider, {
        value: {
          componentMapper: componentMapper,
          validatorMapper: validatorMapperMerged,
          actionMapper: actionMapper,
          formOptions: _objectSpread(_objectSpread(_objectSpread({
            registerInputFile: registerInputFile,
            unRegisterInputFile: unRegisterInputFile,
            pristine: pristine,
            onSubmit: _onSubmit,
            onCancel: onCancel ? function () {
              for (var _len2 = arguments.length, args = new Array(_len2), _key2 = 0; _key2 < _len2; _key2++) {
                args[_key2] = arguments[_key2];
              }

              return onCancel.apply(void 0, [getState().values].concat(args));
            } : undefined,
            onReset: function onReset() {
              _onReset && _onReset.apply(void 0, arguments);
              reset();
            },
            getState: getState,
            valid: valid,
            clearedValue: clearedValue,
            submit: submit,
            handleSubmit: handleSubmit,
            reset: reset,
            clearOnUnmount: clearOnUnmount,
            renderForm: renderForm,
            internalRegisterField: internalRegisterField,
            internalUnRegisterField: internalUnRegisterField
          }, mutators), form), {}, {
            ffGetRegisteredFields: form.getRegisteredFields,
            getRegisteredFields: internalGetRegisteredFields
          })
        }
      }, /*#__PURE__*/React.createElement(FormTemplate, {
        formFields: renderForm(schema.fields),
        schema: schema
      }));
    }
  }));
};

FormRenderer.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func,
  onReset: PropTypes.func,
  schema: PropTypes.object.isRequired,
  clearOnUnmount: PropTypes.bool,
  subscription: PropTypes.shape(_defineProperty({}, PropTypes.string, PropTypes.bool)),
  clearedValue: PropTypes.any,
  componentMapper: PropTypes.shape(_defineProperty({}, PropTypes.string, PropTypes.oneOfType([PropTypes.node, PropTypes.element, PropTypes.func, PropTypes.elementType]))).isRequired,
  FormTemplate: PropTypes.elementType.isRequired,
  validatorMapper: PropTypes.shape(_defineProperty({}, PropTypes.string, PropTypes.func)),
  actionMapper: PropTypes.shape(_defineProperty({}, PropTypes.string, PropTypes.func)),
  schemaValidatorMapper: PropTypes.shape({
    components: PropTypes.shape(_defineProperty({}, PropTypes.string, PropTypes.func)),
    validators: PropTypes.shape(_defineProperty({}, PropTypes.string, PropTypes.func)),
    actions: PropTypes.shape(_defineProperty({}, PropTypes.string, PropTypes.func))
  })
};
FormRenderer.defaultProps = {
  initialValues: {},
  clearOnUnmount: false
};
export default FormRenderer;